import { inject, Injectable } from '@angular/core';
import { map, Observable, shareReplay } from 'rxjs';
import { ApiService } from './api-service';
import { isAdminGroup, IUserDelete, User, UserRole } from '../models/user.model';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private apiService = inject(ApiService);

  private currentUser$: Observable<User> | null = null;

  public getUser(): Observable<User> {
    if (!this.currentUser$) {
      this.currentUser$ = this.apiService.get<User>('/user').pipe(
        shareReplay({ bufferSize: 1, refCount: true })
      );
    }
    return this.currentUser$;
  }


  public isAdmin(): Observable<boolean> {
    return this.getUser().pipe(map(user => isAdminGroup(user?.group)));
  }

  public clearUserCache(): void {
    this.currentUser$ = null;
  }


  public scan(user: User): Observable<User[]> {
    return this.apiService.post<User[]>('/user', user);
  }


  public invite(user: User): Observable<User> {
    return this.apiService.put<User>('/user/invite', user);
  }


  public delete(deleteUser: IUserDelete): Observable<User> {
    return this.apiService.delete<User>('/user', { body: deleteUser });
  }


  public update(user: User): Observable<User> {
    return this.apiService.post<User>('/user/update', user);
  }


  public updateGroup(userRole: UserRole): Observable<string[]> {
    return this.apiService.post<string[]>('/user/update/group', userRole);
  }


  public enable(user: User): Observable<User> {
    return this.apiService.post<User>('/user/enable', user);
  }


  public block(user: User): Observable<User> {
    return this.apiService.post<User>('/user/block', user);
  }


  public alignUser(user: User): Observable<User> {
    return this.apiService.post<User>('/user/align-user', user);
  }


  public getUserByEmail(user: User): Observable<User> {
    return this.apiService.post<User>('/user/find-by-email', user);
  }

  public postSubtenant(subtenantId: string): Observable<{ token: string }> {
    return this.apiService.post<{ token: string }>('/user/select-subtenant', { subtenantId });
  }

  public refreshToken(cognitoToken: string): Observable<{ token: string }> {
    return this.apiService.post<{ token: string }>('/user/refresh-token', {});
  }

  public firstAccess(token: string): Observable<void> {
    return this.apiService.post<void>('/user/first-access', { token });
  }
}
